/* The Neon Factory — creator add-to-cart guard, 1.0.0
   ------------------------------------------------------------------
   The creator prices in the browser, but the ORDER SERVICE decides what can
   actually be bought: #tnf-add POSTs the configuration to <service>/quote and
   only calls /cart/add.js if that answers ok:true.

   When the service says no, the customer was shown nothing at all — the button
   stayed enabled, still read "Add to cart", and the click did nothing. This
   fixes that, without touching the creator's own state machine:

     1. a rejection by the service is explained in place, with a next action,
        and the Add to cart button is relabelled so it never looks like it
        works when it does not;
     2. a connection failure is treated separately from "this needs a quote" —
        one is worth retrying, the other is not;
     3. the design is never cleared. Nothing here touches the text, size,
        colour, board or any other control.

   It also carries two corrections that belong to the same screen:
     - the "Request a quote" link pointed at /pages/contact-us, which is a 404;
      the published page is /pages/contact.
     - the internal "(live add-on)" tag is removed from the price breakdown.

   FORWARD GATE
   Outdoor colour-changing signs are priced by storefront engine 2.6.5 but the
   order service is still on 2.6.4 and rejects them. Rather than let a customer
   find that out by clicking, the button is gated up front. The gate reads the
   service's OWN reported engine version out of any /quote response, so it
   clears itself the moment the service is deployed — no second theme release.

   1.2.0 — ONE gate, and it is a VERSION SKEW ONLY.

   Outdoor RGBW was held in 1.1.0 while its weatherproof controller was
   unconfirmed. Jack Goodwin (merchant) confirmed on 2026-09-22 that TNF RGBW
   signs are outdoor compatible and authorised the hold to be removed. That is a
   merchant confirmation recorded here, not a manufacturer document inspected by
   this file.

   So outdoor RGB and outdoor RGBW are both supported configurations. The only
   remaining reason either could be unbuyable is that the deployed order service
   is still on an engine older than 2.6.6 and would refuse them. That is a
   temporary skew, and this gate clears itself the moment the service reports
   2.6.6 or newer in its own /quote reply — no second theme release.

   There is no unconditional block left in this file. If the service ever does
   refuse a configuration, the refusal itself is shown, with its own wording.

   1.2.1 — ASK THE SERVICE ITS VERSION ON BOOT.

   1.2.0 learned the engine version only from a /quote reply. The creator does
   not call /quote until Add to cart is clicked, and the gate disables Add to
   cart — so on a page that loaded with an outdoor colour-changing design
   selected, nothing could ever clear the gate. Found on the live preview
   immediately after the 2.6.6 deployment: the service was answering 2.6.6 and
   the button still read "Quote required".

   The fix is one GET to the service's own /health on boot. It is read-only,
   exempt from the rate limiter, creates nothing, and carries versions.engine —
   the same number the /quote reply carries. The /quote path is kept as well, so
   the version is corrected the moment a real quote happens either way.
*/
(function () {
  'use strict';

  var MIN_ENGINE_FOR_OUTDOOR_COLOUR = '2.6.6';
  var serverEngine = null;      // learned from the service's own responses
  var engineAsked = false;      // /health asked for once per page
  var lastFailure = null;       // {kind:'blocked'|'network', reason, at}
  var priorState = null;        // the button's own state before we gated it
  var nativeFetch = null;       // the real fetch, captured below and used by askEngine()

  function $(s) { return document.querySelector(s); }

  function cmpVersion(a, b) {
    var x = String(a || '').split('.').map(Number), y = String(b || '').split('.').map(Number);
    for (var i = 0; i < 3; i++) {
      var d = (x[i] || 0) - (y[i] || 0);
      if (d) return d;
    }
    return 0;
  }

  function currentCfg() {
    try { return window.TNF && window.TNF.cfg ? window.TNF.cfg() : null; } catch (e) { return null; }
  }

  function serviceBase() {
    var el = document.getElementById('tnf-creator');
    var u = el && el.getAttribute('data-service');
    return u ? String(u).replace(/\/+$/, '') : '';
  }

  /* Ask the service what engine it is on, once, without creating anything.
     Uses the ORIGINAL fetch so this call is not re-entered by our own wrapper. */
  function askEngine() {
    if (engineAsked) return;
    var base = serviceBase();
    if (!base) return;
    engineAsked = true;
    try {
      (nativeFetch || window.fetch).call(window, base + '/health', { method: 'GET' })
        .then(function (r) { return r.json(); })
        .then(function (b) {
          if (b && b.versions && b.versions.engine) { serverEngine = b.versions.engine; gate(); }
        })
        .catch(function () { engineAsked = false; });   // transient: allow one retry on the next gate()
    } catch (e) { engineAsked = false; }
  }

  /* Is the deployed service still behind the storefront for this design?
     Outdoor colour-changing signs only — everything else has been priceable by
     the service for as long as the storefront has offered it. */
  function aheadOfServer() {
    var c = currentCfg();
    if (!c || !c.outdoor) return false;
    if (c.colourMode !== 'rgb' && c.colourMode !== 'rgbw') return false;
    if (serverEngine === null) return true;                       // assume behind until told
    return cmpVersion(serverEngine, MIN_ENGINE_FOR_OUTDOOR_COLOUR) < 0;
  }

  function reasonText(kind, codes) {
    if (kind === 'network') {
      return 'We could not reach our pricing service just now. Your design is safe — ' +
             'please check your connection and try again in a moment.';
    }
    if (codes && (codes.indexOf('outdoor_premium_rgb_rule_pending') >= 0 ||
                  codes.indexOf('outdoor_rgbw_hardware_unconfirmed') >= 0 ||
                  codes.indexOf('engine_behind_storefront') >= 0)) {
      return 'Outdoor colour-changing signs are not orderable online for a few more ' +
             'minutes while we finish an update. Send us this design and we will price ' +
             'it by hand, usually the same day.';
    }
    return 'We need to price this design by hand. Send it to us and we will come ' +
           'straight back with a quote.';
  }

  function notice() {
    var n = document.getElementById('tnf-cart-notice');
    if (n) return n;
    var add = $('#tnf-add');
    if (!add || !add.parentNode) return null;
    n = document.createElement('div');
    n.id = 'tnf-cart-notice';
    n.className = 'tnf-cart-notice';
    n.setAttribute('role', 'status');
    n.setAttribute('aria-live', 'polite');
    n.hidden = true;
    add.parentNode.insertBefore(n, add);
    return n;
  }

  /* The creator has its own "This design needs a quote from us" panel with its
     own Request a quote button. When it is already on screen our notice is the
     REASON, not a second call to action - so we drop our duplicate button and
     leave one. */
  function creatorAlreadyOffersAQuote() {
    var p = document.querySelector('.tnf-total-blocked');
    if (!p || p.hidden || !p.offsetParent) return false;
    return !!p.querySelector('a, button');
  }

  function showNotice(kind, codes) {
    var n = notice();
    if (!n) return;
    var action = kind === 'network'
      ? '<button type="button" class="tnf-cart-notice__action" data-tnf-retry>Try again</button>'
      : (creatorAlreadyOffersAQuote()
          ? ''
          : '<a class="tnf-cart-notice__action" href="/pages/contact">Request a quote</a>');
    n.innerHTML = '<p class="tnf-cart-notice__text">' + reasonText(kind, codes) + '</p>' + action;
    n.hidden = false;
    var retry = n.querySelector('[data-tnf-retry]');
    if (retry) {
      retry.addEventListener('click', function () {
        lastFailure = null;
        hideNotice();
        var add = $('#tnf-add');
        if (add) add.click();          // the design is untouched; just try again
      });
    }
  }

  function hideNotice() {
    var n = document.getElementById('tnf-cart-notice');
    if (n) { n.hidden = true; n.innerHTML = ''; }
  }

  /* Relabel and disable the button when we know the service will refuse.
     Runs after the creator's own syncAddButton(), so it must be re-applied. */
  function gate() {
    var add = $('#tnf-add');
    if (!add) return;
    if (serverEngine === null) askEngine();

    if (aheadOfServer()) {
      if (add.getAttribute('data-tnf-gated') !== '1') {
        /* remember what we are taking over, so releasing restores it exactly */
        priorState = { disabled: !!add.disabled, label: add.textContent };
        add.setAttribute('data-tnf-gated', '1');
      }
      add.disabled = true;
      add.textContent = 'Quote required';
      showNotice('blocked', ['engine_behind_storefront']);
      return;
    }

    /* Releasing the gate has to HAND THE BUTTON BACK. Removing the marker alone
       left add.disabled true, and the button only came back because the
       creator's own syncAddButton() happened to run afterwards and re-enable
       it — which is luck, not a guarantee. We restore exactly what we took. */
    if (add.getAttribute('data-tnf-gated') === '1') {
      add.removeAttribute('data-tnf-gated');
      if (priorState) {
        add.disabled = priorState.disabled;
        add.textContent = priorState.label;
        priorState = null;
      } else {
        add.disabled = false;
      }
      hideNotice();
    }

    if (lastFailure) showNotice(lastFailure.kind, lastFailure.codes);
  }

  /* Observe the creator's own call to the service. We never change the request
     or the response — we only read the outcome so it can be explained. */
  nativeFetch = window.fetch;
  window.fetch = function (input, init) {
    var url = (typeof input === 'string' ? input : (input && input.url)) || '';
    var isQuote = url.indexOf('/quote') >= 0 && /orders\.theneonfactory\./.test(url);
    if (!isQuote) return nativeFetch.apply(this, arguments);

    return nativeFetch.apply(this, arguments).then(function (res) {
      try {
        res.clone().json().then(function (body) {
          if (body && body.versions && body.versions.engine) serverEngine = body.versions.engine;
          if (body && body.ok === false) {
            lastFailure = { kind: 'blocked', codes: body.blocked || body.blocking || [], at: Date.now() };
            showNotice('blocked', lastFailure.codes);
          } else if (res.ok) {
            lastFailure = null;
            hideNotice();
          }
          gate();
        }).catch(function () { /* not JSON; the HTTP branch below covers it */ });

        if (!res.ok) {
          lastFailure = { kind: 'network', codes: [], at: Date.now() };
          showNotice('network', []);
        }
      } catch (e) { /* never let the guard break the real flow */ }
      return res;
    }, function (err) {
      lastFailure = { kind: 'network', codes: [], at: Date.now() };
      showNotice('network', []);
      throw err;                       // the creator's own handling still runs
    });
  };

  /* Wording and link corrections in the price breakdown, re-applied on render. */
  function tidy(root) {
    (root || document).querySelectorAll('.tnf-total-blocked a[href="/pages/contact-us"], a.tnf-btn-ghost[href="/pages/contact-us"]')
      .forEach(function (a) { a.setAttribute('href', '/pages/contact'); });
    (root || document).querySelectorAll('#tnf-total em, .tnf-total em, .tnf-total-list em').forEach(function (em) {
      if (/live add-on/i.test(em.textContent) && em.parentNode) em.parentNode.removeChild(em);
    });
  }

  function boot() {
    tidy();
    gate();
    var target = $('#tnf-total') || $('#tnf-creator') || document.body;
    if (window.MutationObserver) {
      new MutationObserver(function () { tidy(); gate(); }).observe(target, { childList: true, subtree: true });
    }
    document.addEventListener('click', function () { setTimeout(gate, 120); }, true);
    setInterval(gate, 1500);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
